import React, { useState } from "react";
import { LayoutDashboard, FileText, Megaphone, LogOut, Lock, KeyRound, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Logo } from "../components/Shared";
import Dashboard from "../components/admin/Dashboard";
import SurveysTable from "../components/admin/SurveysTable";
import SessionsTable from "../components/admin/SessionsTable";
import AdsTracking from "../components/admin/AdsTracking";
import NotifySettingsDialog from "../components/admin/NotifySettingsDialog";
import { login, changePassword, getToken, clearToken, setToken } from "../api";

export default function Admin({ defaultTab = "dashboard" }) {
  const [authed, setAuthed] = useState(() => !!getToken());
  const [tab, setTab] = useState(defaultTab);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  const tabs = [
    { key: "dashboard", label: "儀表板", icon: LayoutDashboard },
    { key: "surveys", label: "問卷資料", icon: FileText },
    { key: "sessions", label: "未完成問卷", icon: Hourglass },
    { key: "ads", label: "廣告成效", icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 border-b border-border bg-white px-4 py-3">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-7" />
            <span className="hidden text-sm font-bold text-muted-foreground sm:block">管理後台</span>
          </div>
          <div className="flex items-center gap-2">
            <NotifySettingsDialog />
            <ResetPasswordDialog />
            <Button variant="ghost" size="sm" onClick={() => { clearToken(); setAuthed(false); }}>
              <LogOut className="mr-1 h-4 w-4" /> 登出
            </Button>
          </div>
        </div>
      </header>

      <div className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl gap-1 px-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition ${
                tab === t.key ? "border-brand text-brand" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {tab === "dashboard" && <Dashboard />}
        {tab === "surveys" && <SurveysTable />}
        {tab === "sessions" && <SessionsTable />}
        {tab === "ads" && <AdsTracking />}
      </main>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("請輸入 Email");
    setLoading(true);
    try {
      const res = await login(email, pw);
      setToken(res.token);
      toast.success("登入成功");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "密碼錯誤");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#FAFAFA] px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-white p-6 shadow-lg">
        <div className="mb-6 text-center">
          <Logo className="mx-auto h-9" />
          <p className="mt-3 flex items-center justify-center gap-1.5 text-sm font-bold text-muted-foreground">
            <Lock className="h-4 w-4" /> 管理後台登入
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-sm">Email 帳號</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@lakeze.com" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm">密碼</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="請輸入密碼" />
          </div>
          <Button type="submit" disabled={loading} className="h-11 w-full bg-brand font-bold text-white hover:bg-[#EA580C]">
            {loading ? "登入中…" : "登入"}
          </Button>
          <p className="text-center text-xs text-muted-foreground">預設密碼：admin123（登入後可於右上角重設）</p>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordDialog() {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");

  const save = async () => {
    if (newPw.length < 6) return toast.error("新密碼至少 6 碼");
    try {
      await changePassword(oldPw, newPw);
      setOpen(false); setOldPw(""); setNewPw("");
      toast.success("密碼已更新");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "更新失敗");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <KeyRound className="mr-1 h-4 w-4" /> 重設密碼
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>重設管理密碼</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-sm">目前密碼</Label>
            <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">新密碼（至少 6 碼）</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>取消</Button>
          <Button onClick={save} className="bg-brand text-white hover:bg-[#EA580C]">更新</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
